import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

if (!API_URL) {
  console.error('REACT_APP_BACKEND_URL environment variable is not set');
}

const API_BASE = `${API_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
};

// Helper function to convert snake_case to camelCase for children
const transformChild = (c) => ({
  id: c.id,
  userId: c.user_id,
  name: c.name,
  dateOfBirth: c.date_of_birth,
  gender: c.gender,
  avatarColor: c.avatar_color
});

// Children APIs
export const childrenAPI = {
  create: async (childData) => {
    // Convert camelCase to snake_case for API
    const apiData = {
      name: childData.name,
      date_of_birth: childData.dateOfBirth,
      gender: childData.gender,
      avatar_color: childData.avatarColor
    };
    const response = await api.post('/children', apiData);
    return transformChild(response.data);
  },

  getAll: async () => {
    const response = await api.get('/children');
    return response.data.map(transformChild);
  },

  getById: async (childId) => {
    const response = await api.get(`/children/${childId}`);
    return transformChild(response.data);
  },

  update: async (childId, childData) => {
    // Convert camelCase to snake_case for API
    const apiData = {};
    if (childData.name) apiData.name = childData.name;
    if (childData.dateOfBirth) apiData.date_of_birth = childData.dateOfBirth;
    if (childData.gender) apiData.gender = childData.gender;
    if (childData.avatarColor) apiData.avatar_color = childData.avatarColor;
    
    const response = await api.put(`/children/${childId}`, apiData);
    return transformChild(response.data);
  },

  delete: async (childId) => {
    const response = await api.delete(`/children/${childId}`);
    return response.data;
  },
};

// Helper function to convert snake_case to camelCase for measurements
const transformMeasurement = (m) => ({
  id: m.id,
  childId: m.child_id,
  date: m.date,
  lengthCm: m.length_cm,
  widthCm: m.width_cm,
  method: m.method,
  usSize: m.us_size,
  euSize: m.eu_size,
  ukSize: m.uk_size,
  ageMonths: m.age_months
});

// Measurements APIs
export const measurementsAPI = {
  create: async (measurementData) => {
    // Convert camelCase to snake_case for API
    const apiData = {
      child_id: measurementData.childId,
      date: measurementData.date,
      length_cm: measurementData.lengthCm,
      width_cm: measurementData.widthCm,
      method: measurementData.method,
      us_size: measurementData.usSize,
      eu_size: measurementData.euSize,
      uk_size: measurementData.ukSize,
      age_months: measurementData.ageMonths
    };
    const response = await api.post('/measurements', apiData);
    return transformMeasurement(response.data);
  },

  getByChildId: async (childId) => {
    const response = await api.get(`/measurements/${childId}`);
    return response.data.map(transformMeasurement);
  },

  getLatest: async (childId) => {
    const response = await api.get(`/measurements/${childId}/latest`);
    return transformMeasurement(response.data);
  },

  analyzeImage: async (imageBase64, referenceType = 'credit_card') => {
    const response = await api.post('/analyze-foot-image', {
      image_base64: imageBase64,
      reference_type: referenceType
    });
    return response.data;
  },
};

// Payments APIs
export const paymentsAPI = {
  createCheckout: async (childId) => {
    const originUrl = window.location.origin;
    const response = await api.post(`/payments/create-checkout?child_id=${childId}&origin_url=${encodeURIComponent(originUrl)}`);
    return response.data;
  },

  checkStatus: async (sessionId) => {
    const response = await api.get(`/payments/status/${sessionId}`);
    return response.data;
  },

  checkPayment: async (childId) => {
    const response = await api.get(`/payments/check/${childId}`);
    return response.data;
  },
};

// Subscription APIs
export const subscriptionAPI = {
  createCheckout: async () => {
    const originUrl = window.location.origin;
    const response = await api.post(`/subscription/create-checkout?origin_url=${encodeURIComponent(originUrl)}`);
    return response.data;
  },

  getStatus: async () => {
    const response = await api.get('/subscription/status');
    return response.data;
  },

  verify: async (sessionId) => {
    const response = await api.post(`/subscription/verify/${sessionId}`);
    return response.data;
  },

  cancel: async () => {
    const response = await api.post('/subscription/cancel');
    return response.data;
  },
};

export default api;
